/**
 * The web path the password-reset email links to. Single source of truth for
 * both the React Navigation linking config (`app-navigator.tsx`) and the
 * startup session check in `authContext` (TA-2593, TA-11134).
 */
export const PASSWORD_RESET_PATH = 'users/password/edit';

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, '');

// scheme://authority/path?query#hash
const ABSOLUTE_URL = /^([a-z][a-z\d+.-]*):\/\/([^/?#]*)([^?#]*)(\?[^#]*)?/i;
const WEB_SCHEME = /^https?$/i;

const getQueryParam = (query: string, name: string): string | null => {
  for (const pair of query.replace(/^\?/, '').split('&')) {
    const eq = pair.indexOf('=');
    const key = eq === -1 ? pair : pair.slice(0, eq);
    if (key !== name) continue;
    try {
      return decodeURIComponent(
        (eq === -1 ? '' : pair.slice(eq + 1)).replace(/\+/g, ' ')
      );
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * The reset token carried by a password-reset link, or `null` when `href` is
 * not one (wrong path, missing or too-short token, relative or unparseable).
 *
 * Parsed by hand rather than with `URL`: React Native's `URL` does not
 * implement `pathname`/`searchParams` on every runtime, and this also runs on
 * native for Universal / App Links (TA-11134).
 *
 * The token length check mirrors the guard in `ResetPassword.web.tsx`: React
 * Navigation hands `:token?`-style params through as a single `=` when the
 * query string is malformed, and a 1-character token is never a real one.
 */
export const getPasswordResetToken = (
  href: string | undefined | null
): string | null => {
  if (!href) return null;
  const match = ABSOLUTE_URL.exec(href);
  if (!match) return null;
  const [, scheme, authority, path = '', query = ''] = match;
  // Custom schemes have no host. React Navigation strips `tasktag://`, so in
  // `tasktag://users/password/edit` the "authority" is really the first path
  // segment (`tasktag:///users/...` just has an empty one).
  const pathname = WEB_SCHEME.test(scheme) ? path : `${authority}${path}`;
  if (trimSlashes(pathname) !== PASSWORD_RESET_PATH) return null;
  const token = getQueryParam(query, 'reset_password_token') ?? '';
  return token.length > 1 ? token : null;
};

/** True when `href` is a password-reset link carrying a usable token. */
export const isPasswordResetUrl = (href: string | undefined | null): boolean =>
  getPasswordResetToken(href) !== null;

/** Current browser URL, or `''` off-web / before the DOM exists. */
export const getCurrentHref = (): string =>
  typeof globalThis !== 'undefined' ? (globalThis.location?.href ?? '') : '';
